import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useWorkspaceStore } from '../stores/workspaceStore'
import { useOpenAsk } from '../shell/ShellContext'
import { suggestionsFor } from '../shell/askSuggestions'
import type { ShopifyCustomer, ShopifyProduct } from '../services/dataService'
import type { LeastSureItem } from '../services/api'
import {
  countOf,
  custMeta,
  custName,
  exactCount,
  fmtCurrency,
  IndexBound,
  lifecycleStyle,
  productKey,
  useIndex,
  type IndexState,
  type Lifecycle,
} from '../components/knowledge/knowledgeIndex'
import {
  Badge,
  Button,
  Card,
  Chip,
  EmptyState,
  EntryList,
  EntryRow,
  ErrorState,
  LoadingState,
  PageHeader,
  SearchField,
  Section,
  SegmentedControl,
  Stack,
  Suggestions,
  TabPanel,
  Tabs,
} from '../ds'
import LiveFeed from '../components/knowledge/LiveFeed'
import LeastSure, { countText, useLeastSure, useLeastSureVerdicts } from '../components/knowledge/LeastSure'
import LeastSurePatterns, { patternMemberKeys, useLeastSurePatterns } from '../components/knowledge/LeastSurePatterns'
import LearnedTimeline, { LEARNED_PARAM, resolveLearnedFilter, writeLearnedFilter } from '../components/knowledge/LearnedTimeline'
import KnowledgeInboxBanner from '../components/knowledge/KnowledgeInboxBanner'
import MerchantAnswers, { useMerchantAnswers } from '../components/knowledge/MerchantAnswers'
import KnowledgeDrawer from '../components/knowledge/KnowledgeDrawer'
import { useKnowledgeDrawers } from '../components/knowledge/useKnowledgeDrawers'

/**
 * Knowledge — what she believes, and where she got it (Knowledge v6 PR 1, MAR-325).
 *
 * Replaces the pre-v6 `Memory.tsx` ("Who she remembers."). The work spec is
 * `docs/work/knowledge-v6.md`; §4 lists every v6 element and either where it ships or why it
 * does not. In this file:
 *
 * - **Places** (MAR-383 flattened the two layers into one row; MAR-401 replaced that row with
 *   the design's tabs + Who, below, on the same URL contract). A place is
 *   `?scope=customers|products|answers`, `?view=learned` or `?view=feed`
 *   (`resolveKnowledgePlace`, written by `writeKnowledgePlace`).
 *   Every older spelling still lands: `?tab=customers|products|feed`, `?view=beliefs`
 *   (+ `scope`), and a pre-MAR-383 feed link that also carried a `scope`. `view` and
 *   `scope`, when present, win over a legacy `tab`; `view=feed` wins over a `scope`.
 * - **Scopes** Customers / Products / Your answers, in `?scope=`. ⚠️ The design's
 *   "Everything" scope is deliberately absent (MAR-327): there is no REST search, the index
 *   search reaches loaded pages only, and `findCustomers` is Ask-only. A count is printed
 *   ONLY when it is exact (the scale line, `exactCount`).
 * - **Idle** (no query, To check): "One customer/product at a time" (`LeastSure`), listing
 *   ONLY the Who's subjects — Customers people, Products products — then the "Or ask her"
 *   row, last. **Searching**: the loaded-pages index search, with the sentence
 *   that says how far it reaches (`IndexBound`).
 * - **Your answers**: `MerchantAnswers`, moved here from Account → Notifications.
 * - **What she learned** (MAR-389): `?view=learned`, its own place — every belief she formed,
 *   newest first, with Confirmed / Corrected / Expired filters (`LearnedTimeline`). `view`
 *   wins over a `scope`, like the feed. The filter is `?learned=<status>` beside it, so a
 *   filtered list is a link; leaving the place drops it.
 * - **Patterns** (MAR-389): above the least-sure list, the same belief word for word about
 *   more than one subject (`LeastSurePatterns`, `?group=type`), settled one by one or together.
 *
 * ── v6 conformance (MAR-401, the canvas's Knowledge-1…4) ────────────────────────────────────
 *
 * The one row of chips became the design's two controls on one rule: underline Tabs —
 * **To check** (with the server's to-check count as its badge, `N` or `N+`, never a bare `N` for
 * a floor) · **What she learned** · **Your answers** — and, standing on the same rule, the
 * **Who** SegmentedControl (Customers | Products). The URL contract did not change: To check is
 * `?scope=customers|products` (the default), What she learned `?view=learned`, Your answers
 * `?scope=answers`, Raw events `?view=feed`. `knowledgeTabOf` / `knowledgeWhoOf` read the
 * design's two controls off that contract, so every older link lands where it did.
 *  - Who is a qualifier, so it rides along wherever the merchant goes and To check returns to
 *    it: as `scope=customers|products` beside `view=learned|feed` (the view still wins), and as
 *    `who=products` beside `scope=answers`, whose `scope` it cannot share (`WHO_PARAM`).
 *  - ⚠️ Who is drawn on To check only. The timeline read is workspace-wide (`GET …/timeline`
 *    has no scope), so a Who on What she learned could only filter LOADED pages — and then
 *    "That is everything — N beliefs" would be false. Your answers has no subject at all.
 *  - Live feed left the row: it is "Raw events", reached from What she learned's footer, under
 *    its own breadcrumb ("What she learned / Raw events") and title, with no search or tabs.
 *  - The index chip counts went with the chips; the scale line still prints the exact ones.
 *
 * ── Reads: only what the view on screen renders ─────────────────────────────────────────
 *
 * Nothing is read for a view or scope the merchant is not looking at (the old screen read
 * both 100-row indexes, the least-sure read and the answers on every mount):
 *  - the feed view reads the feed alone (`LiveFeed`);
 *  - To check reads least-sure (and patterns) for the current Who only
 *    (`?scope=customer|product`); the answers only on Your answers;
 *  - a customer/product index page only once a query is typed on that scope.
 * What is kept, and for how long, differs by read — stated rather than implied:
 *  - least-sure is kept per (workspace, subject) and the answers per workspace, for as long
 *    as this screen is mounted, so switching Who or tabs back costs nothing;
 *  - each index (`usePagedRead`) holds ONE slot — the last workspace it read; returning to
 *    a workspace after another re-reads it;
 *  - `LiveFeed` unmounts off the feed view, so returning to the feed re-reads it, and its
 *    single slot holds only the current source.
 * ⚠️ The consequence is deliberate: the To check badge and the scale line appear only once
 * the read behind them has landed AND is exact — absent before then, never a guess. The
 * Inbox banner reads the shell's queue, which the shell holds anyway.
 *
 * ⚠️ Gone with the old screen, and deliberately: the Advanced toggle (`body.memadv`) and
 * the "Raw record · debug" section. The drawer's tiles and engagement Signal went in PR 1
 * and came back in PR 2 by a user decision (spec §2 decision 3a). MAR-36 later removed the
 * stale nightly churn score; there is still no header confidence and no synthesized number.
 *
 * ── The drawer (MAR-326) ────────────────────────────────────────────────────────────────
 *
 * ONE drawer for a person or a product, deep-linkable: `?customer=<id>` / `?product=<id>`,
 * owned by `useKnowledgeDrawers`. Every opener below goes through it, so the URL, the slot
 * and the Ask pin move together.
 *
 * ⚠️ The URL is the only source of truth for view and scope — no `useState` mirror, as
 * `screens/Authority.tsx` does it — so Back walks the views and a link arriving while the
 * screen is open changes the screen, not just the URL. Writes MERGE into the existing
 * params: a link carrying why the merchant is here keeps it.
 */

type View = 'beliefs' | 'feed' | 'learned'
type Scope = 'customers' | 'products' | 'answers'
/** Where the merchant is: one of the scopes, the timeline, or the feed. */
export type KnowledgePlace = Scope | 'learned' | 'feed'
const SCOPES: Scope[] = ['customers', 'products', 'answers']
/** Places that are a `view` of their own rather than a `scope` of the beliefs view. */
const VIEW_PLACES: readonly KnowledgePlace[] = ['learned', 'feed']

/** The design's view tabs (MAR-401). Raw events is not one: it is reached from What she learned. */
export type KnowledgeTab = 'check' | 'learned' | 'answers'
/** The design's "Who" control: whose beliefs To check lists. */
export type KnowledgeWho = 'customers' | 'products'
const TAB_LABEL: Record<KnowledgeTab, string> = {
  check: 'To check',
  learned: 'What she learned',
  answers: 'Your answers',
}
const TABS: KnowledgeTab[] = ['check', 'learned', 'answers']

/** The tab a place sits under — `null` for Raw events, which is drawn without the tabs. */
export function knowledgeTabOf(place: KnowledgePlace): KnowledgeTab | null {
  if (place === 'customers' || place === 'products') return 'check'
  if (place === 'feed') return null
  return place
}

/**
 * Where Who is kept while the URL's `scope` names Your answers (`scope=answers&who=products`,
 * PR #359 review) — `scope` cannot hold both, and Who must survive a trip to Your answers.
 */
export const WHO_PARAM = 'who'

/**
 * Whose beliefs the Who control says — `products` when the URL's scope says so, or when Your
 * answers is holding it (`who=products`); every other spelling (none, `customers`, a legacy
 * `tab`) is Customers.
 */
export function knowledgeWhoOf(params: URLSearchParams): KnowledgeWho {
  const scope = resolveKnowledgeLocation(params).scope
  if (scope === 'products') return 'products'
  return scope === 'answers' && params.get(WHO_PARAM) === 'products' ? 'products' : 'customers'
}
/** A person's own lifecycle reading, as a chip tone. A fact about the row, never a filter. */
const LIFECYCLE_TONE: Record<Lifecycle, 'gold' | 'sage' | 'danger' | 'neutral'> = {
  vip: 'gold',
  loyal: 'sage',
  risk: 'danger',
  new: 'neutral',
}

/**
 * Where the URL says the merchant is. Exported for the routing test; a pure function of
 * the params so a legacy link and its v6 spelling provably resolve to the same place.
 */
export function resolveKnowledgeLocation(params: URLSearchParams): { view: View; scope: Scope } {
  const view = params.get('view')
  const scope = params.get('scope')
  const tab = params.get('tab')
  const resolvedView: View =
    view === 'feed' || view === 'beliefs' || view === 'learned' ? view : tab === 'feed' ? 'feed' : 'beliefs'
  const resolvedScope: Scope = SCOPES.includes(scope as Scope)
    ? (scope as Scope)
    : tab === 'products'
      ? 'products'
      : 'customers'
  return { view: resolvedView, scope: resolvedScope }
}

/**
 * The one place a URL names (MAR-383). Built on `resolveKnowledgeLocation`, so every older
 * spelling — `?tab=`, `?view=beliefs`, a feed link that also carried a `scope` — lands
 * exactly where it landed before the two layers became one row.
 */
export function resolveKnowledgePlace(params: URLSearchParams): KnowledgePlace {
  const { view, scope } = resolveKnowledgeLocation(params)
  return view === 'feed' || view === 'learned' ? view : scope
}

/**
 * The params that move the merchant to `place`, MERGED into the current ones: anything a
 * link carried (why the merchant is here) rides along; the legacy `tab` and the param of the
 * place being left are dropped, so the URL holds one spelling and never two that disagree.
 *
 * ⚠️ The drawer's params do NOT ride along. A place change PUSHES, and a pushed entry
 * carrying `?customer=` is one Back lands on after the drawer was closed (a replace) —
 * reopening a record the merchant had closed. Changing place closes the drawer instead.
 * ⚠️ Except a drawer MASKED under this workspace (its `ws` names another): it is not on
 * screen, so there is nothing here to close, and dropping its params would erase it —
 * A → B, change place, → A would come back to nothing. Escape is unbound while masked for
 * the same reason (MAR-205); a place change must not be the one way to break it. Accepted
 * trade (PR #257 re-review): those params then ride the pushed entry, so after the merchant
 * returns to A and closes the drawer there, Back lands on the entry that was current when the
 * drawer was open, and it reopens — as Back to any entry with the drawer open does.
 */
export function writeKnowledgePlace(
  current: URLSearchParams,
  place: KnowledgePlace,
  workspaceId: string | undefined
): URLSearchParams {
  const next = new URLSearchParams(current)
  next.delete('tab')
  const drawerWs = next.get('ws')
  if (!drawerWs || drawerWs === workspaceId) {
    next.delete('customer')
    next.delete('product')
    next.delete('ws')
  }
  // The timeline's filter belongs to its place alone.
  if (place !== 'learned') next.delete(LEARNED_PARAM)
  // Who is a qualifier (MAR-401): it rides along wherever the merchant goes, so To check
  // returns to it — as `scope` under a view (the view still wins, `resolveKnowledgePlace`), and
  // as `who` beside Your answers, whose `scope` it cannot share. A legacy `?tab=products`
  // names Who too, and is carried over in the v6 spelling. Customers is the default, so it is
  // written only where the link already spelled it.
  const who = knowledgeWhoOf(current)
  next.delete(WHO_PARAM)
  if (VIEW_PLACES.includes(place)) {
    next.set('view', place)
    if (who === 'products' || current.get('scope') === 'customers') next.set('scope', who)
    else next.delete('scope')
  } else {
    next.set('scope', place)
    next.delete('view')
    if (place === 'answers' && who === 'products') next.set(WHO_PARAM, who)
  }
  return next
}

/** The scale line: only the counts that are exact, and nothing at all when none is. */
function scaleLine(customers: number | null, products: number | null): string | null {
  const parts: string[] = []
  if (customers != null) parts.push(countOf(customers, 'customer'))
  if (products != null) parts.push(countOf(products, 'product'))
  return parts.length ? `${parts.join(', ')}.` : null
}

export default function Knowledge() {
  const { currentWorkspace, currentAccount } = useWorkspaceStore()
  const workspaceId = currentWorkspace?.id
  const openAsk = useOpenAsk()

  const [searchParams, setSearchParams] = useSearchParams()
  const { view, scope } = resolveKnowledgeLocation(searchParams)
  const place = resolveKnowledgePlace(searchParams)
  const tab = knowledgeTabOf(place)
  const who = knowledgeWhoOf(searchParams)
  /** Write the URL — and nothing else; no local clear rides along. */
  const go = (to: KnowledgePlace) => setSearchParams(writeKnowledgePlace(searchParams, to, workspaceId))

  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()

  // ⚠️ Each read is enabled only while what it feeds is on screen (see the docblock).
  const beliefs = view === 'beliefs'
  const customerIndex = useIndex<ShopifyCustomer>(
    'customers', workspaceId, currentAccount?.id, beliefs && scope === 'customers' && !!q
  )
  const productIndex = useIndex<ShopifyProduct>(
    'products', workspaceId, currentAccount?.id, beliefs && scope === 'products' && !!q
  )
  // One server read per Who (`?scope=`), made when To check shows it.
  const subject = scope === 'products' ? 'product' : 'customer'
  const leastSure = useLeastSure(workspaceId, subject, beliefs && scope !== 'answers' && !q)
  // Held here, beside the snapshot it overlays, so a verdict outlives the list's unmount
  // (Raw events, What she learned, Your answers, a search) and the row never comes back unsettled.
  const leastSureVerdicts = useLeastSureVerdicts(workspaceId)
  const answers = useMerchantAnswers(workspaceId, beliefs && scope === 'answers')
  const drawers = useKnowledgeDrawers(workspaceId, currentAccount?.id)

  // ⚠️ NOT defaulted to `[]` here: `x ?? []` is a fresh array per render while loading, and
  // both memos take it as a dependency. The default lives inside each memo.
  const customers = customerIndex.list?.items
  const products = productIndex.list?.items
  const customerHits = useMemo(
    () =>
      q
        ? (customers ?? []).filter((c) => custName(c).toLowerCase().includes(q) || c.email?.toLowerCase().includes(q))
        : [],
    [customers, q]
  )
  const productHits = useMemo(
    () => (q ? (products ?? []).filter((p) => p.title?.toLowerCase().includes(q)) : []),
    [products, q]
  )

  // Patterns ride the same window as least-sure, read on the same tab and the same Who.
  const patterns = useLeastSurePatterns(workspaceId, subject, beliefs && scope !== 'answers' && !q)

  const scale = scaleLine(exactCount(customerIndex.list), exactCount(productIndex.list))
  // The To check badge: the server's count as it stated it — `N`, or `N+` for a floor — for the
  // CURRENT Who (it is that subject's least-sure read). It exists only once To check has been
  // read for that Who in this visit: a merchant who lands on What she learned or Your answers
  // sees no badge until they open To check (the read is deferred, never made for a badge).
  // Absent until then, never inferred from the rows (MAR-389, MAR-401). Nothing to check is
  // no badge rather than a "0" (MAR-412): the list below says it in words.
  const toCheckText = leastSure.state === 'loaded' ? countText(leastSure.data?.count) : null
  const toCheck = toCheckText === '0' || toCheckText === '0+' ? null : toCheckText
  // A belief a pattern card lists is not listed again one by one (MAR-401).
  const patternKeys = useMemo(
    () => patternMemberKeys(patterns.state === 'loaded' ? patterns.data : null),
    [patterns.state, patterns.data]
  )
  const onTab = (t: string) => go(t === 'check' ? who : (t as KnowledgePlace))

  /** A least-sure row opens the drawer for its subject. */
  const openLeastSure = (item: LeastSureItem) => {
    if (item.scope === 'customer') {
      // The drawer is opened from the LABEL the least-sure read verified — a name and a
      // masked email, and nothing else; the full record is fetched on open, and the header
      // line and tiles wait for it. A withheld name stays withheld — the header says so
      // rather than falling back to anything that might be an address.
      const l = item.label as
        | { customerId: string | null; firstName: string | null; lastName: string | null; emailMasked: string | null }
        | null
      const name = l ? `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.emailMasked || 'Customer' : 'Name unavailable'
      void drawers.openCustomerLabel({
        customerId: l?.customerId || item.id,
        name,
        email: l?.emailMasked || undefined,
      })
      return
    }
    // A LABEL, not a catalogue row: the id and the title the read verified. No row is
    // invented for it, so the drawer shows no "variants"/"in stock" it never read.
    const title = (item.label as { title: string | null } | null)?.title ?? null
    drawers.openProductLabel({ productId: item.id, title })
  }

  const askAboutQuery = () => openAsk(`What does Marva know about “${query.trim()}”?`)
  const asks = suggestionsFor('knowledge', null)

  const panelTab: KnowledgeTab = tab ?? 'check'
  return (
    <div className="mv-canvas-wide">
      {view === 'feed' ? (
        <>
          {/* Raw events (MAR-401): its own header, under What she learned — the one way here. */}
          <Stack gap="sm">
            <nav aria-label="Breadcrumb" className="t-small" data-testid="kn-feed-crumb">
              <Button variant="link" onClick={() => go('learned')}>
                What she learned
              </Button>{' '}
              / <span aria-current="page">Raw events</span>
            </nav>
            <PageHeader
              title="What’s coming in"
              sub="The records she reads, as they arrive. For checking the connection, not for reading: what she makes of them is in What she learned."
            />
          </Stack>
          <LiveFeed workspaceId={workspaceId} accountId={currentAccount?.id} />
        </>
      ) : (
        <>
      <PageHeader
        eyebrow="Knowledge"
        title={
          <>
            What she believes, <span className="mv-em">and where she got it.</span>
          </>
        }
        sub={
          <>
            {scale && <span data-testid="kn-scale">{scale} </span>}A memory of every customer, not a segment of them.
            Ask for a name, a product, or a question she was asked.
          </>
        }
      />

      {/* Search first, then the view tabs with Who on their rule (MAR-401). The search finds
          customers, products and answers; the timeline has no search route, so it is not
          offered there rather than offered and ignored. */}
      <Stack gap="sm">
        {view === 'beliefs' && (
          <SearchField
            size="lg"
            value={query}
            onChange={setQuery}
            placeholder="A customer, a product, or something a customer asked"
            label="Search what she knows"
          />
        )}
        <Tabs
          idBase="kn"
          label="Knowledge views"
          value={panelTab}
          onChange={onTab}
          tabs={TABS.map((t) => ({
            value: t,
            label: TAB_LABEL[t],
            ...(t === 'check' && toCheck != null ? { badge: toCheck } : {}),
          }))}
          aside={
            panelTab === 'check' ? (
              <SegmentedControl
                size="sm"
                label="Who"
                value={who}
                onChange={(w) => go(w as KnowledgeWho)}
                options={[
                  { value: 'customers', label: 'Customers' },
                  { value: 'products', label: 'Products' },
                ]}
              />
            ) : undefined
          }
        />
      </Stack>

      <TabPanel idBase="kn" value="learned" active={panelTab} variant="underline">
        <LearnedTimeline
          workspaceId={workspaceId}
          onOpen={openLeastSure}
          filter={resolveLearnedFilter(searchParams)}
          onFilter={(f) => setSearchParams(writeLearnedFilter(searchParams, f), { replace: true })}
          onRawEvents={() => go('feed')}
        />
      </TabPanel>
      <TabPanel idBase="kn" value="answers" active={panelTab} variant="underline">
        <Stack data-testid="kn-beliefs-view">
          <KnowledgeInboxBanner />
          <MerchantAnswers read={answers} query={query} />
        </Stack>
      </TabPanel>
      <TabPanel idBase="kn" value="check" active={panelTab} variant="underline">
        <Stack data-testid="kn-beliefs-view">
          <KnowledgeInboxBanner />

          {!q ? (
            <>
              {scope === 'products' && (
                <Card tone="recessed" padding="sm" data-testid="kn-products-lead">
                  <p className="t-small">
                    <strong>She doesn’t use product beliefs in messages yet.</strong> She forms them from what many
                    customers do and ask — never from one person.
                  </p>
                </Card>
              )}
              <LeastSurePatterns
                read={patterns}
                onOpen={openLeastSure}
                workspaceId={workspaceId}
                verdicts={leastSureVerdicts}
              />
              <LeastSure
                read={leastSure}
                scope={subject}
                onOpen={openLeastSure}
                workspaceId={workspaceId}
                verdicts={leastSureVerdicts}
                exclude={patternKeys}
              />
              {/* Last in the view, one row of pills, as the canvas draws it (MAR-401). */}
              <Suggestions placement="page" layout="row" label="Or ask her" items={asks} onPick={(label) => openAsk(label)} />
            </>
          ) : scope === 'customers' ? (
            <Results
              noun="customer"
              list={customerIndex.list}
              query={query}
              hits={customerHits.length}
              onLoadMore={customerIndex.loadMore}
              onRetry={customerIndex.retry}
              onAsk={() => openAsk()}
              onAskAbout={askAboutQuery}
            >
              {customerHits.map((c) => {
                const ls = lifecycleStyle(c)
                return (
                  <EntryRow
                    testId="kn-row"
                    data-scope="customer"
                    key={c.customerId || c.email}
                    onOpen={() => void drawers.openCustomer(c)}
                    title={custName(c)}
                    tag={c.customerId ? <Badge mono>{c.customerId}</Badge> : undefined}
                    why={
                      <>
                        {custMeta(c)} · {fmtCurrency(c.totalSpent)} lifetime
                      </>
                    }
                    clamp={false}
                    // No pill when the row carries no intelligence: an unread stage is not "New".
                    chips={
                      ls ? (
                        <Chip tone={LIFECYCLE_TONE[ls.cls]} data-lifecycle={ls.cls}>
                          {ls.label}
                        </Chip>
                      ) : undefined
                    }
                  />
                )
              })}
            </Results>
          ) : (
            <Results
              noun="product"
              list={productIndex.list}
              query={query}
              hits={productHits.length}
              onLoadMore={productIndex.loadMore}
              onRetry={productIndex.retry}
              onAsk={() => openAsk()}
              onAskAbout={askAboutQuery}
            >
              {productHits.map((p) => (
                <EntryRow
                  testId="kn-row"
                  data-scope="product"
                  key={productKey(p) || p.handle}
                  onOpen={() => drawers.openProduct(p)}
                  title={p.title}
                  tag={productKey(p) ? <Badge mono>{productKey(p)}</Badge> : undefined}
                  why={
                    [p.vendor, p.productType, p.variantCount != null ? `${p.variantCount} variants` : null]
                      .filter(Boolean)
                      .join(' · ') || undefined
                  }
                  clamp={false}
                  chips={p.status ? <Chip data-testid="kn-tag">{p.status}</Chip> : undefined}
                />
              ))}
            </Results>
          )}
        </Stack>
      </TabPanel>
        </>
      )}

      {/* THE DRAWER — mounted unconditionally; `open` is the only thing that changes. */}
      <KnowledgeDrawer
        shown={drawers.shown}
        workspaceId={workspaceId}
        onClose={drawers.close}
        onExited={drawers.onExited}
        onRetry={drawers.retryCustomer}
        onAsk={() => openAsk()}
        onRelabel={drawers.relabel}
      />
    </div>
  )
}

/**
 * A search over one loaded index: "Looking…" while its first page is in flight, a refusal
 * as a refusal, the matches, and always the sentence that says how far the search reached.
 *
 * ⚠️ "Nothing on X" is only said without qualification when the index is the whole
 * workspace (no cursor). Over a partial load it names what was searched, because the
 * person may be on a page not loaded yet.
 */
function Results({
  noun,
  list,
  query,
  hits,
  onLoadMore,
  onRetry,
  onAsk,
  onAskAbout,
  children,
}: {
  noun: 'customer' | 'product'
  list: IndexState<unknown> | null
  query: string
  hits: number
  onLoadMore: () => void
  onRetry: () => void
  onAsk: () => void
  onAskAbout: () => void
  children: React.ReactNode
}) {
  if (!list) return <LoadingState caption="Looking…" />
  if (list.failed) {
    /* ⚠️ Not "Nothing on …". A refused read and an empty workspace are different facts. */
    return (
      <ErrorState
        title={`Could not load your ${noun === 'customer' ? 'customers' : 'catalogue'} just now, so nothing is shown rather than a list that looks complete.`}
        onRetry={onRetry}
      />
    )
  }
  const loaded = list.items.length
  return (
    <Section
      as="div"
      description={
        hits > 0 ? (
          <span data-testid="kn-results-h">
            {hits} of the {countOf(loaded, noun)} loaded match{hits === 1 ? 'es' : ''}
          </span>
        ) : undefined
      }
    >
      {hits > 0 ? (
        // On the surface, in a bordered card, like every list on this page (MAR-401).
        <Card padding="sm">
          <EntryList label={noun === 'customer' ? 'Matching customers' : 'Matching products'}>{children}</EntryList>
        </Card>
      ) : (
        <EmptyState
          size="sm"
          action={
            <Button variant="ghost" size="sm" onClick={onAskAbout}>
              Ask her about it
            </Button>
          }
        >
          {list.hasMore ? (
            <>
              Nothing on “{query.trim()}” in the {countOf(loaded, noun)} loaded here.
            </>
          ) : (
            <>Nothing on “{query.trim()}”. She holds a record once someone asks about it or buys it.</>
          )}
        </EmptyState>
      )}
      <IndexBound noun={noun} list={list} onLoadMore={onLoadMore} onAsk={onAsk} />
    </Section>
  )
}
